/**
 * usePortcoData — Shared data-fetching hook for PortcoDetail page.
 *
 * Now wraps React Query hooks for caching, deduplication, and
 * background refresh. Maintains the same return interface for
 * backward compatibility with existing consumers.
 */

import { usePortcoAllData } from "./useQueries";
import type {
  PortcoMetrics,
  WeeklyTrendPoint,
  MonthlyTrendPoint,
  ForecastAccuracyData,
  InventoryTrendPoint,
} from "../types/api";

export interface PortcoData {
  metrics: PortcoMetrics | null;
  weeklyTrend: WeeklyTrendPoint[] | null;
  monthlyTrend: MonthlyTrendPoint[] | null;
  forecastAccuracy: ForecastAccuracyData | null;
  inventoryTrend: InventoryTrendPoint[] | null;
  loading: boolean;
  error: string | null;
}

/**
 * Fetch all portco data in parallel using React Query.
 *
 * @param portcoId  — slug from the URL params
 * @param enabled   — gate fetching (e.g. wait for MSAL interaction to finish)
 * @param _onAuthError — kept for backward compatibility but no longer used
 *                       (React Query retries handle transient auth issues)
 * @param reportDate — optional YYYY-MM-DD; when set, the core metrics query
 *                     fetches the dashboard "as of" that date.
 */
export function usePortcoData(
  portcoId: string | undefined,
  enabled: boolean = true,
  _onAuthError?: (error: unknown) => void,
  reportDate?: string,
): PortcoData {
  return usePortcoAllData(portcoId, enabled, reportDate);
}
